/*
 * @ubm/ui/components/table — LA peinture des tableaux Foundry : une seule définition, deux structures.
 *
 *  · `Table` (ce dossier) : vrai <table>, layout natif, rendu serveur pur. Il pose `.root` / `.header` / `.body` / `.footer`
 *    sur ses éléments ; ses lignes et cellules sont visées par la structure (`.body > tr > td`), sans classe propre.
 *  · DataTable (`@ubm/product-ui`, moteur client) : grille flex `role="grid"`. Elle attache les mêmes classes (`.root`,
 *    `.headRow`, `.headCell`, `.row`, `.cell`, `.numeric`, `.empty`, `.compact`) à ses éléments historiques
 *    `.ubm-datatable__*`, via `tablePresentation`. Sa STRUCTURE et ses états (flex, largeurs, gouttière, gel, tri, sélection,
 *    édition, clavier) restent dans `@ubm/product-ui/styles.css` ; ils lisent ici les seules valeurs géométriques communes.
 *
 * Reconduit depuis la cascade FINALE de la DataTable (styles.css avant ce lot : base 17–47, compacte 92–120, en-tête 186–228,
 * cellule 626–641, numérique 2873–2879, vide 2896–2905, peinture UIArc 2958–3015). Les spécificités d'origine sont gardées
 * (survol 0,2,0 puis 0,3,0 sous pointeur fin ; compacte 0,2,0) : les peaux d'hôte (Apple, 0,3,0+) gagnent toujours.
 * Aucune valeur nouvelle, aucun jeton global nouveau : les variables `--ubm-table-*` sont locales au nœud racine.
 */

/* ═══ Contrat commun (Table ET DataTable) ═══════════════════════════════════════════════════════════════════════════ */

.root {
  /* Géométrie partagée : `height` d'une ligne de <table> (extensible au contenu), `min-height` d'une ligne de grille. */
  --ubm-table-head-h: 34px;
  --ubm-table-row-h: 35px;
  --ubm-table-cell-px: 9px;
  --ubm-table-empty-h: 120px;
  /* Fond de cellule opaque UIArc : relu par le survol ci-dessous et par les états de la grille (styles.css). */
  --ubm-table-cell-bg: var(--arc-surface, var(--surface-base));
  background: var(--ubm-table-cell-bg);
  border-top: 1px solid var(--border-default);
}
/* Typo et encre de la racine : spécificité NULLE. Une portée d'hôte posée SUR la racine — `.ubm-mac:where(…)` (0,1,0) de la
   présentation Apple, qui fixe police et encre du kit — l'emporte QUEL QUE SOIT l'ordre de chargement du module, comme
   avant ce lot (feuille macOS chargée après styles.css). Mesuré au navigateur : en 0,1,0 l'encre dépendait de l'ordre. */
:where(.root) {
  font-size: 13px;
  color: var(--arc-foreground, var(--text-default));
}

/* Densité compacte (table utilitaire) : même peinture, rangées de 32, cellules de 8, filets verticaux, en-tête en casse
   normale. Côté grille, posée par la classe consommable historique `ubm-datatable--compact`. */
.root.compact {
  --ubm-table-head-h: 32px;
  --ubm-table-row-h: 32px;
  --ubm-table-cell-px: 8px;
  border-top: 0;
}

/* Ligne d'en-tête : filet appuyé, encre atténuée. */
.headRow,
.header > tr {
  border-bottom: 1px solid var(--arc-border, var(--border-default));
  color: var(--arc-text-muted, var(--text-muted));
}

/* Libellé de colonne : petite capitale espacée, jamais une ligne de plus. */
.headCell,
.header > tr > th {
  color: var(--text-faint);
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.compact .headCell,
.compact > .header > tr > th {
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
  font-weight: 450;
  color: var(--text-muted);
  border-right: 1px solid var(--border-muted);
}

/* Ligne de données : filet discret, fond qui suit le survol. */
.row,
.body > tr,
.footer > tr {
  border-bottom: 1px solid var(--arc-border-subtle, var(--border-default));
  transition: background-color var(--arc-duration-standard, 0.24s) var(--arc-ease-standard, ease);
}
.row:hover,
.body > tr:hover {
  background: var(--surface-hover);
}
@media (hover: hover) and (pointer: fine) {
  .root .row:hover,
  .root > .body > tr:hover {
    background: color-mix(in oklab, var(--arc-foreground, var(--text-default)) 3%, var(--ubm-table-cell-bg));
  }
}
@media (prefers-reduced-motion: reduce) {
  .root .row,
  .root > .body > tr,
  .root > .footer > tr {
    transition: none;
  }
}

.compact .cell,
.compact > :is(.body, .footer) > tr > :is(td, th) {
  border-right: 1px solid var(--border-muted);
}

/* Chiffres : alignés à droite, chasse fixe. 0,2,0 : passe devant l'alignement de départ des cellules natives (0,1,2). */
.root .numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* État vide : le message reste celui du consommateur. */
.root .empty {
  padding: 24px;
  color: var(--text-muted);
  text-align: center;
}

/* ═══ Structure HTML native — Table seulement (les éléments de la grille ne sont jamais visés ici) ═════════════════════ */

table.root {
  width: 100%;
  /* `collapse` : les filets portés par les <tr> (contrat commun) ne se dessinent que dans ce modèle. */
  border-collapse: collapse;
  border-spacing: 0;
}
.header > tr {
  height: var(--ubm-table-head-h);
}
.body > tr,
.footer > tr {
  height: var(--ubm-table-row-h);
}
.header > tr > th {
  padding: 0 var(--ubm-table-cell-px);
  text-align: start;
  vertical-align: middle;
  white-space: nowrap;
}
/* Marge verticale DÉRIVÉE de la hauteur de ligne commune : une ligne d'un seul texte mesure exactement 35 (32 en compact),
   comme la grille ; une cellule riche (lien + description) s'étend avec une respiration symétrique (filet bas de 1 px). */
.body > tr > :is(td, th),
.footer > tr > :is(td, th) {
  padding: max(0px, calc((var(--ubm-table-row-h) - 1lh - 1px) / 2)) var(--ubm-table-cell-px);
  text-align: start;
  vertical-align: middle;
  font-weight: inherit;
}
.root :is(td, th).numeric {
  white-space: nowrap;
}
/* Totaux : séparés des données par le filet d'en-tête, chiffres appuyés. */
.footer {
  border-top: 1px solid var(--arc-border, var(--border-default));
}
.footer > tr > :is(td, th) {
  font-weight: 600;
}
.body > tr > .empty {
  height: var(--ubm-table-empty-h);
}

.caption {
  caption-side: top;
  padding: 0 0 8px;
  text-align: start;
  color: var(--text-muted);
}

/* Défilement horizontal borné au tableau (jamais la page) ; région focusable et nommée pour le clavier. */
.container {
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
}
.container:focus-visible {
  outline: var(--focus-ring-width, 2px) solid var(--focus-ring);
  outline-offset: -2px;
}
